import React, { useState, useMemo, useCallback } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  Video,
  Plus,
  List,
  Grid3X3,
} from 'lucide-react'
import { useCalendarEvents, useCalendarMutations } from '@/lib/data/hooks/useCalendar'
import { useAuth } from '@/lib/auth/context'
import { CalendarEvent, CalendarViewMode, EVENT_COLORS } from '@/types/calendar'
import { EventModal } from '@/components/calendar/EventModal'

export function CalendarView() {
  const { adminUser } = useAuth()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [viewMode, setViewMode] = useState<CalendarViewMode>('week')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedSlot, setSelectedSlot] = useState<{ date: Date; hour: number } | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [hoveredEventId, setHoveredEventId] = useState<string | null>(null)

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    const start = new Date(currentDate)
    const end = new Date(currentDate)

    if (viewMode === 'week') {
      const day = start.getDay()
      const diff = start.getDate() - day + (day === 0 ? -6 : 1)
      start.setDate(diff)
      start.setHours(0, 0, 0, 0)
      end.setDate(start.getDate() + 6)
      end.setHours(23, 59, 59, 999)
    } else if (viewMode === 'month') {
      start.setDate(1)
      start.setHours(0, 0, 0, 0)
      end.setMonth(end.getMonth() + 1, 0)
      end.setHours(23, 59, 59, 999)
    } else {
      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)
    }

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    }
  }, [currentDate, viewMode])

  // Fetch events
  const { events, isLoading, refetch } = useCalendarEvents({
    ...dateRange,
    ownerId: adminUser?.id,
  })

  // Calculate week days
  const weekDays = useMemo(() => {
    const start = new Date(dateRange.startDate)
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start)
      d.setDate(d.getDate() + i)
      return d
    })
  }, [dateRange.startDate])

  // Hours array (6 AM to 10 PM)
  const hours = Array.from({ length: 17 }, (_, i) => i + 6)

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    const d = new Date(currentDate)
    if (viewMode === 'week') {
      d.setDate(d.getDate() - 7)
    } else if (viewMode === 'month') {
      d.setMonth(d.getMonth() - 1)
    } else {
      d.setDate(d.getDate() - 1)
    }
    setCurrentDate(d)
  }, [currentDate, viewMode])

  const handleNext = useCallback(() => {
    const d = new Date(currentDate)
    if (viewMode === 'week') {
      d.setDate(d.getDate() + 7)
    } else if (viewMode === 'month') {
      d.setMonth(d.getMonth() + 1)
    } else {
      d.setDate(d.getDate() + 1)
    }
    setCurrentDate(d)
  }, [currentDate, viewMode])

  const handleToday = useCallback(() => {
    setCurrentDate(new Date())
  }, [])

  // Slot click handler
  const handleSlotClick = useCallback((day: Date, hour: number) => {
    setSelectedSlot({ date: day, hour })
    setSelectedEvent(null)
    setIsModalOpen(true)
  }, [])

  // Event click handler
  const handleEventClick = useCallback((event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedEvent(event)
    setSelectedSlot(null)
    setIsModalOpen(true)
  }, [])

  // Modal close handler
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
    setSelectedSlot(null)
    setSelectedEvent(null)
  }, [])

  // After event save
  const handleEventSaved = useCallback(() => {
    handleModalClose()
    refetch()
  }, [handleModalClose, refetch])

  // Get events for a specific day
  const getEventsForDay = useCallback((day: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.startTime)
      return (
        eventDate.getDate() === day.getDate() &&
        eventDate.getMonth() === day.getMonth() &&
        eventDate.getFullYear() === day.getFullYear()
      )
    })
  }, [events])

  // Format header based on view mode
  const headerText = useMemo(() => {
    if (viewMode === 'week') {
      const start = new Date(dateRange.startDate)
      return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    } else if (viewMode === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    } else {
      return currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    }
  }, [currentDate, viewMode, dateRange.startDate])

  return (
    <div className="h-[calc(100vh-140px)] flex flex-col bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl overflow-hidden animate-fade-in shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-[var(--border)]">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold text-[var(--text-main)] flex items-center gap-2">
            <CalendarIcon className="text-[var(--primary)]" />
            Calendar
          </h2>

          {/* Navigation */}
          <div className="flex items-center bg-[var(--bg-element)] rounded-lg border border-[var(--border)] p-1">
            <button
              onClick={handlePrevious}
              className="p-1 hover:bg-[var(--bg-hover)] rounded-md transition-colors text-[var(--text-muted)] hover:text-[var(--text-main)]"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={handleToday}
              className="px-3 py-1 text-sm font-medium text-[var(--text-main)] hover:bg-[var(--bg-hover)] rounded-md transition-colors"
            >
              Today
            </button>
            <button
              onClick={handleNext}
              className="p-1 hover:bg-[var(--bg-hover)] rounded-md transition-colors text-[var(--text-muted)] hover:text-[var(--text-main)]"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <span className="text-lg font-medium text-[var(--text-main)]">{headerText}</span>
        </div>

        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-[var(--bg-element)] rounded-lg border border-[var(--border)] p-1">
            <button
              onClick={() => setViewMode('day')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'day'
                  ? 'bg-[var(--primary)] text-white'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              Day
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'week'
                  ? 'bg-[var(--primary)] text-white'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'month'
                  ? 'bg-[var(--primary)] text-white'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              Month
            </button>
          </div>

          {/* New Event Button */}
          <button
            onClick={() => {
              setSelectedSlot({ date: new Date(), hour: 9 })
              setSelectedEvent(null)
              setIsModalOpen(true)
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-opacity font-medium shadow-lg shadow-[var(--primary)]/20"
          >
            <Plus size={18} /> New Event
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      {viewMode === 'week' || viewMode === 'day' ? (
        <WeekGrid
          days={viewMode === 'day' ? [currentDate] : weekDays}
          hours={hours}
          events={events}
          onSlotClick={handleSlotClick}
          onEventClick={handleEventClick}
          hoveredEventId={hoveredEventId}
          onEventHover={setHoveredEventId}
          isLoading={isLoading}
        />
      ) : (
        <MonthGrid
          currentDate={currentDate}
          events={events}
          onDayClick={(day) => {
            setCurrentDate(day)
            setViewMode('day')
          }}
          onEventClick={handleEventClick}
          hoveredEventId={hoveredEventId}
          onEventHover={setHoveredEventId}
          isLoading={isLoading}
        />
      )}

      {/* Event Modal */}
      <EventModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSaved={handleEventSaved}
        selectedSlot={selectedSlot}
        selectedEvent={selectedEvent}
      />
    </div>
  )
}

// ============================================================================
// Week Grid Component
// ============================================================================

interface WeekGridProps {
  days: Date[]
  hours: number[]
  events: CalendarEvent[]
  onSlotClick: (day: Date, hour: number) => void
  onEventClick: (event: CalendarEvent, e: React.MouseEvent) => void
  hoveredEventId: string | null
  onEventHover: (eventId: string | null) => void
  isLoading: boolean
}

function WeekGrid({ days, hours, events, onSlotClick, onEventClick, hoveredEventId, onEventHover, isLoading }: WeekGridProps) {
  return (
    <div className="flex-1 overflow-auto custom-scrollbar flex relative">
      {/* Time Column */}
      <div className="w-16 flex-shrink-0 border-r border-[var(--border)] bg-[var(--bg-card)] sticky left-0 z-20">
        <div className="h-12 border-b border-[var(--border)]"></div>
        {hours.map(hour => (
          <div key={hour} className="h-20 text-xs text-[var(--text-muted)] text-right pr-2 pt-2 relative">
            {hour > 12 ? hour - 12 : hour} {hour >= 12 ? 'PM' : 'AM'}
          </div>
        ))}
      </div>

      {/* Days Columns */}
      <div className="flex-1 flex min-w-[800px]">
        {days.map((day, dayIndex) => {
          const isToday = day.toDateString() === new Date().toDateString()
          const dayEvents = events.filter(event => {
            const eventDate = new Date(event.startTime)
            return (
              eventDate.getDate() === day.getDate() &&
              eventDate.getMonth() === day.getMonth() &&
              eventDate.getFullYear() === day.getFullYear()
            )
          })

          return (
            <div
              key={dayIndex}
              className="flex-1 min-w-[120px] border-r border-[var(--border)] last:border-r-0 relative"
            >
              {/* Day Header */}
              <div
                className={`h-12 border-b border-[var(--border)] flex flex-col items-center justify-center sticky top-0 z-10 bg-[var(--bg-card)] ${
                  isToday ? 'bg-[var(--bg-element)]' : ''
                }`}
              >
                <span
                  className={`text-xs font-semibold uppercase tracking-wider ${
                    isToday ? 'text-[var(--primary)]' : 'text-[var(--text-muted)]'
                  }`}
                >
                  {day.toLocaleDateString('en-US', { weekday: 'short' })}
                </span>
                <div
                  className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-bold mt-1 ${
                    isToday ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-main)]'
                  }`}
                >
                  {day.getDate()}
                </div>
              </div>

              {/* Time Slots */}
              <div className="relative">
                {hours.map(hour => (
                  <div
                    key={hour}
                    onClick={() => onSlotClick(day, hour)}
                    className="h-20 border-b border-[var(--border)] hover:bg-[var(--bg-hover)]/30 transition-colors cursor-pointer"
                  />
                ))}

                {/* Current Time Indicator */}
                {isToday && (
                  <CurrentTimeIndicator hours={hours} />
                )}

                {/* Events */}
                {dayEvents.map(event => (
                  <EventBlock
                    key={event.id}
                    event={event}
                    onClick={onEventClick}
                    isHovered={hoveredEventId === event.id}
                    hasHoveredEvent={hoveredEventId !== null}
                    onHover={onEventHover}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* Hover overlay - darkens background when event is hovered */}
      {hoveredEventId && (
        <div
          className="absolute inset-0 bg-black/30 pointer-events-none z-10 transition-opacity duration-200"
          style={{ opacity: hoveredEventId ? 1 : 0 }}
        />
      )}
    </div>
  )
}

// ============================================================================
// Month Grid Component
// ============================================================================

interface MonthGridProps {
  currentDate: Date
  events: CalendarEvent[]
  onDayClick: (day: Date) => void
  onEventClick: (event: CalendarEvent, e: React.MouseEvent) => void
  hoveredEventId: string | null
  onEventHover: (eventId: string | null) => void
  isLoading: boolean
}

function MonthGrid({ currentDate, events, onDayClick, onEventClick, hoveredEventId, onEventHover, isLoading }: MonthGridProps) {
  const { weeks, daysOfWeek } = useMemo(() => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startOffset = firstDay.getDay()

    const weeks: Date[][] = []
    let currentWeek: Date[] = []

    // Fill in days from previous month
    for (let i = startOffset - 1; i >= 0; i--) {
      const d = new Date(year, month, -i)
      currentWeek.push(d)
    }

    // Fill in days of current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      if (currentWeek.length === 7) {
        weeks.push(currentWeek)
        currentWeek = []
      }
      currentWeek.push(new Date(year, month, day))
    }

    // Fill in days from next month
    let nextMonthDay = 1
    while (currentWeek.length < 7) {
      currentWeek.push(new Date(year, month + 1, nextMonthDay++))
    }
    weeks.push(currentWeek)

    return {
      weeks,
      daysOfWeek: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    }
  }, [currentDate])

  const getEventsForDay = useCallback((day: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.startTime)
      return (
        eventDate.getDate() === day.getDate() &&
        eventDate.getMonth() === day.getMonth() &&
        eventDate.getFullYear() === day.getFullYear()
      )
    })
  }, [events])

  return (
    <div className="flex-1 overflow-auto custom-scrollbar p-4">
      {/* Days of Week Header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {daysOfWeek.map(day => (
          <div
            key={day}
            className="text-center text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Weeks */}
      <div className="grid grid-rows-6 gap-1 flex-1">
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 gap-1">
            {week.map((day, dayIndex) => {
              const isToday = day.toDateString() === new Date().toDateString()
              const isCurrentMonth = day.getMonth() === currentDate.getMonth()
              const dayEvents = getEventsForDay(day)

              return (
                <div
                  key={dayIndex}
                  onClick={() => onDayClick(day)}
                  className={`min-h-[100px] p-2 rounded-lg border border-[var(--border)] cursor-pointer transition-colors ${
                    isCurrentMonth
                      ? 'bg-[var(--bg-element)] hover:bg-[var(--bg-hover)]'
                      : 'bg-[var(--bg-main)] opacity-50'
                  }`}
                >
                  <div
                    className={`w-6 h-6 flex items-center justify-center rounded-full text-sm font-medium mb-1 ${
                      isToday
                        ? 'bg-[var(--primary)] text-white'
                        : isCurrentMonth
                          ? 'text-[var(--text-main)]'
                          : 'text-[var(--text-muted)]'
                    }`}
                  >
                    {day.getDate()}
                  </div>

                  {/* Events */}
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map(event => {
                      const isHovered = hoveredEventId === event.id
                      const hasHoveredEvent = hoveredEventId !== null
                      return (
                        <div
                          key={event.id}
                          onClick={(e) => {
                            e.stopPropagation()
                            onEventClick(event, e)
                          }}
                          onMouseEnter={() => onEventHover(event.id)}
                          onMouseLeave={() => onEventHover(null)}
                          className={`text-xs px-1.5 py-0.5 rounded truncate cursor-pointer transition-all duration-200 ${
                            isHovered
                              ? 'scale-105 shadow-lg ring-2 ring-white/50'
                              : hasHoveredEvent
                                ? 'opacity-40'
                                : ''
                          }`}
                          style={{
                            backgroundColor: EVENT_COLORS[event.color]?.bg || 'var(--bg-hover)',
                            borderLeft: `2px solid ${EVENT_COLORS[event.color]?.border || 'var(--primary)'}`,
                            color: EVENT_COLORS[event.color]?.text || 'var(--text-main)',
                            position: isHovered ? 'relative' : undefined,
                            zIndex: isHovered ? 50 : undefined,
                          }}
                        >
                          {event.title}
                        </div>
                      )
                    })}
                    {dayEvents.length > 3 && (
                      <div className="text-xs text-[var(--text-muted)] pl-1">
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// Event Block Component
// ============================================================================

interface EventBlockProps {
  event: CalendarEvent
  onClick: (event: CalendarEvent, e: React.MouseEvent) => void
  isHovered: boolean
  hasHoveredEvent: boolean
  onHover: (eventId: string | null) => void
}

function EventBlock({ event, onClick, isHovered, hasHoveredEvent, onHover }: EventBlockProps) {
  const startDate = new Date(event.startTime)
  const endDate = new Date(event.endTime)
  const startHour = startDate.getHours()
  const startMin = startDate.getMinutes()
  const durationHours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60)
  const top = (startHour - 6) * 80 + (startMin / 60) * 80
  // Minimum height of 40px so even short events are visible
  const height = Math.max(durationHours * 80, 40)
  const isCompact = height < 50

  if (top < 0 || startHour < 6 || startHour > 22) return null

  const colors = EVENT_COLORS[event.color] || EVENT_COLORS.primary

  return (
    <div
      onClick={(e) => onClick(event, e)}
      onMouseEnter={() => onHover(event.id)}
      onMouseLeave={() => onHover(null)}
      className={`absolute left-1 right-1 rounded-lg p-2 border-l-4 text-xs overflow-hidden cursor-pointer transition-all duration-200 shadow-sm backdrop-blur-sm ${
        isHovered
          ? 'scale-105 shadow-2xl ring-2 ring-white/50 brightness-110'
          : hasHoveredEvent
            ? 'opacity-40 brightness-75'
            : 'hover:brightness-110 hover:shadow-md'
      }`}
      style={{
        top: `${top}px`,
        height: `${height - 2}px`,
        backgroundColor: colors.bg,
        borderLeftColor: colors.border,
        color: colors.text,
        zIndex: isHovered ? 50 : 10,
      }}
      title={`${event.title} (${startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`}
    >
      {isCompact ? (
        // Compact layout for short events
        <div className="flex items-center gap-2 h-full">
          <span className="font-semibold truncate flex-1">{event.title}</span>
          <span className="opacity-80 text-[10px] whitespace-nowrap">
            {startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
          {event.hasVideoConference && <Video size={12} className="opacity-90 flex-shrink-0" />}
        </div>
      ) : (
        // Standard layout for longer events
        <>
          <div className="font-semibold truncate">{event.title}</div>
          <div className="opacity-80 truncate flex items-center gap-1 mt-0.5">
            <Clock size={10} />
            {startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
          {event.hasVideoConference && (
            <div className="mt-1 flex items-center gap-1 opacity-90">
              <Video size={10} /> Video
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ============================================================================
// Current Time Indicator
// ============================================================================

function CurrentTimeIndicator({ hours }: { hours: number[] }) {
  const now = new Date()
  const currentHour = now.getHours()
  const currentMin = now.getMinutes()

  if (currentHour < hours[0] || currentHour > hours[hours.length - 1]) {
    return null
  }

  const top = (currentHour - hours[0]) * 80 + (currentMin / 60) * 80

  return (
    <div
      className="absolute w-full border-t-2 border-red-500 z-20 pointer-events-none"
      style={{ top: `${top}px` }}
    >
      <div className="absolute -left-1.5 -top-1.5 w-3 h-3 bg-red-500 rounded-full" />
    </div>
  )
}

export default CalendarView
